import { useEffect, useRef } from 'react';

/**
 * Abort/reset in-progress forms when the active company changes.
 * Prevents saving parties/items from company A into company B.
 *
 * @param {Object} options
 * @param {() => boolean} options.isDirty - Whether the form has unsaved work
 * @param {() => void} options.onSwitch - Called when company switches while dirty (or always if resetAlways)
 * @param {boolean} [options.resetAlways=false] - Call onSwitch even when form is clean
 * @param {(message: string) => void} [options.notify] - Optional user-facing notice
 */
export function useCompanySwitchGuard({ isDirty, onSwitch, resetAlways = false, notify } = {}) {
  const isDirtyRef = useRef(isDirty);
  const onSwitchRef = useRef(onSwitch);
  const notifyRef = useRef(notify);

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  useEffect(() => {
    onSwitchRef.current = onSwitch;
  }, [onSwitch]);

  useEffect(() => {
    notifyRef.current = notify;
  }, [notify]);

  useEffect(() => {
    const handleSwitch = (event) => {
      const dirty = typeof isDirtyRef.current === 'function' ? isDirtyRef.current() : !!isDirtyRef.current;
      if (!dirty && !resetAlways) return;

      try {
        notifyRef.current?.(
          'Company switched — the open form was cleared to avoid saving data into the wrong company.'
        );
      } catch {
        /* ignore notify failures */
      }

      try {
        onSwitchRef.current?.(event?.detail || {});
      } catch (err) {
        console.error('companySwitched guard handler failed:', err);
      }
    };

    window.addEventListener('companySwitched', handleSwitch);
    return () => window.removeEventListener('companySwitched', handleSwitch);
  }, [resetAlways]);
}

export default useCompanySwitchGuard;
